import React, { useEffect, useState, useRef } from "react";
import { Pencil, Trash2, Plus, Save, Upload, X, GripVertical, Star, StarOff, FileText, CheckCircle2, XCircle, Mail, Send, Download, Eye, EyeOff, Archive } from "lucide-react";
import { api, eur, formatErr } from "@/lib/api";
import { fileToCompressedDataURL } from "@/lib/imageUpload";
import ImageField from "@/components/ImageField";
import { useContent } from "@/context/ContentContext";

const emptyForm = { name: "", description: "", price: "", price_b2b: "", category: "Bijuteria", subcategory: "", image_url: "", stock: 10, weight_g: "", featured: false, active: true, archived: false };

const emptySettings = {
  store_name: "", tagline: "", email: "", phone: "", address: "", nif: "",
  iban: "", bank_name: "", instagram: "", facebook: "",
  shipping_cost: 0, free_shipping_threshold: 0, return_policy_days: 14,
};

const emptyContent = {
  hero: { eyebrow: "", title_line1: "", title_line2: "", description: "", image_url: "", cta_primary: "", cta_secondary: "" },
  benefits: [{ title: "", text: "" }, { title: "", text: "" }, { title: "", text: "" }],
  categories_section: { eyebrow: "", title: "", items: [] },
  featured_section: { eyebrow: "", title: "" },
  editorial_cta: { eyebrow: "", title: "", description: "", cta_text: "" },
  nav_menu: [],
  registration_fields: [],
  header: { logo_text: "Au and Co." },
  footer: { columns: [], payment_line: "" },
  announcement: { active: false, text: "", link: "" },
};

export default function Admin() {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const [showForm, setShowForm] = useState(false);

  // Settings state
  const [settings, setSettings] = useState(emptySettings);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState("");

  // Image upload state
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [imgErr, setImgErr] = useState("");

  // Content state
  const { content: liveContent, refresh: refreshContent } = useContent();
  const [content, setContent] = useState(emptyContent);
  const [savingContent, setSavingContent] = useState(false);
  const [contentMsg, setContentMsg] = useState("");
  const [categories, setCategories] = useState([]);

  // Payments state
  const [paySettings, setPaySettings] = useState({
    mbway: { active: true, number: "", instructions: "" },
    transfer: { active: true, holder: "", iban: "", bic: "", instructions: "" },
    shipping_tiers: [],
    shipping_over_message: "",
    payment_page_message: "",
  });
  const [savingPay, setSavingPay] = useState(false);
  const [paySettingsMsg, setPaySettingsMsg] = useState("");

  const loadPaySettings = async () => {
    try {
      const { data } = await api.get("/admin/payment-settings");
      setPaySettings((cur) => ({ ...cur, ...data }));
    } catch { /* noop */ }
  };
  useEffect(() => { if (tab === "payments") loadPaySettings(); }, [tab]);

  const savePaySettings = async (e) => {
    e.preventDefault();
    setSavingPay(true);
    setPaySettingsMsg("");
    try {
      await api.put("/admin/payment-settings", paySettings);
      setPaySettingsMsg("Definições de pagamento guardadas com sucesso.");
      setTimeout(() => setPaySettingsMsg(""), 3000);
    } catch (er) {
      setPaySettingsMsg(formatErr(er.response?.data?.detail) || er.message);
    } finally { setSavingPay(false); }
  };

  const togglePayment = async (orderId, newStatus) => {
    try {
      await api.patch(`/admin/orders/${orderId}/payment-status`, { payment_status: newStatus });
      load();
    } catch (e) { alert(formatErr(e.response?.data?.detail) || e.message); }
  };

  const setOrderStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}/status`, { status });
      load();
    } catch (e) { alert(formatErr(e.response?.data?.detail) || e.message); }
  };

  const setUserStatus = async (u, action) => {
    setRegBusy((b) => ({ ...b, [u.id]: true }));
    try {
      await api.post(`/admin/users/${u.id}/${action}`);
      await loadRegistrations();
    } catch (e) { alert(formatErr(e.response?.data?.detail) || e.message); }
    finally { setRegBusy((b) => ({ ...b, [u.id]: false })); }
  };

  const setUserRole = async (u, role) => {
    try {
      await api.post(`/admin/users/${u.id}/role`, { role });
      await loadRegistrations();
    } catch (e) { alert(formatErr(e.response?.data?.detail) || e.message); }
  };

  // Registrations / emails / broadcast state
  const [registrations, setRegistrations] = useState([]);
  const [regStatusFilter, setRegStatusFilter] = useState("pending");
  const [regBusy, setRegBusy] = useState({});
  const [emailTpls, setEmailTpls] = useState({
    welcome: { enabled: true, subject: "", body_html: "" },
    approved: { enabled: true, subject: "", body_html: "" },
    rejected: { enabled: true, subject: "", body_html: "" },
    payment_confirmed: { enabled: true, subject: "", body_html: "" },
  });
  const [savingTpls, setSavingTpls] = useState(false);
  const [tplsMsg, setTplsMsg] = useState("");
  const [broadcast, setBroadcast] = useState({ subject: "", body_html: "", attachments: [] });
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastMsg, setBroadcastMsg] = useState("");

  // Shipping (zones/modalities/tiers) state
  const [shipping, setShipping] = useState(null);
  const [savingShip, setSavingShip] = useState(false);
  const [shipMsg, setShipMsg] = useState("");
  const loadShipping = async () => {
    try { const { data } = await api.get("/admin/shipping"); setShipping(data); } catch { /* noop */ }
  };
  useEffect(() => { if (tab === "shipping" && !shipping) loadShipping(); }, [tab]);
  const saveShipping = async (e) => {
    e.preventDefault();
    setSavingShip(true); setShipMsg("");
    try {
      await api.put("/admin/shipping", shipping);
      setShipMsg("Portes guardados com sucesso.");
      setTimeout(() => setShipMsg(""), 3000);
    } catch (er) { setShipMsg(formatErr(er.response?.data?.detail) || er.message); }
    finally { setSavingShip(false); }
  };
  const mutShip = (fn) => setShipping((cur) => { const c = structuredClone(cur); fn(c); return c; });
  const addZone = () => mutShip((c) => c.zones.push({ id: `zone-${Date.now()}`, name: "Nova zona internacional", region: "international", active: true, countries: [], modalities: [] }));
  const addModality = (zi) => mutShip((c) => c.zones[zi].modalities.push({ id: `m-${Date.now()}`, name: "Nova modalidade", carrier: "", delivery_time: "", active: true, message: "", tiers: [] }));
  const addTier = (zi, mi) => mutShip((c) => c.zones[zi].modalities[mi].tiers.push({ id: `t-${Date.now()}`, min_g: 0, max_g: null, price: 0, active: true }));

  // Pages CMS state
  const [pages, setPages] = useState([]);
  const [pageForm, setPageForm] = useState(null); // {id?, slug, title, body_html, image_url, published, seo_title, seo_description}
  const [pageMsg, setPageMsg] = useState("");
  const loadPages = async () => {
    try { const { data } = await api.get("/admin/pages"); setPages(data); } catch { setPages([]); }
  };
  useEffect(() => { if (tab === "pages") loadPages(); }, [tab]);
  const savePage = async (e) => {
    e.preventDefault();
    setPageMsg("");
    try {
      if (pageForm.id) await api.put(`/admin/pages/${pageForm.id}`, pageForm);
      else await api.post("/admin/pages", pageForm);
      setPageForm(null);
      await loadPages();
    } catch (er) { setPageMsg(formatErr(er.response?.data?.detail) || er.message); }
  };
  const deletePage = async (id) => {
    if (!window.confirm("Eliminar esta página?")) return;
    try { await api.delete(`/admin/pages/${id}`); await loadPages(); }
    catch (e) { alert(formatErr(e.response?.data?.detail) || e.message); }
  };

  const loadRegistrations = async () => {
    try {
      const url = regStatusFilter ? `/admin/registrations?status=${regStatusFilter}` : "/admin/registrations";
      const { data } = await api.get(url);
      setRegistrations(data);
    } catch { setRegistrations([]); }
  };
  useEffect(() => { if (tab === "registrations") loadRegistrations(); }, [tab, regStatusFilter]);

  const loadEmailTpls = async () => {
    try {
      const { data } = await api.get("/admin/email-templates");
      setEmailTpls((cur) => ({ ...cur, ...data }));
    } catch { /* noop */ }
  };
  useEffect(() => { if (tab === "emails") loadEmailTpls(); }, [tab]);

  const approveReg = async (u) => {
    setRegBusy((b) => ({ ...b, [u.id]: true }));
    try {
      await api.post(`/admin/registrations/${u.id}/approve`);
      await loadRegistrations();
    } catch (e) { alert(formatErr(e.response?.data?.detail) || e.message); }
    finally { setRegBusy((b) => ({ ...b, [u.id]: false })); }
  };
  const rejectReg = async (u) => {
    const reason = window.prompt("Motivo da recusa (opcional):", "") || "";
    setRegBusy((b) => ({ ...b, [u.id]: true }));
    try {
      await api.post(`/admin/registrations/${u.id}/reject`, { reason });
      await loadRegistrations();
    } catch (e) { alert(formatErr(e.response?.data?.detail) || e.message); }
    finally { setRegBusy((b) => ({ ...b, [u.id]: false })); }
  };
  const downloadUserFile = async (uid, fieldKey, filename) => {
    try {
      const { data } = await api.get(`/admin/users/${uid}/file/${fieldKey}`);
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${data.content_base64}`;
      link.download = data.filename || filename || "documento.pdf";
      link.click();
    } catch (e) { alert("Erro ao descarregar ficheiro."); }
  };

  const saveEmailTpls = async (e) => {
    e.preventDefault();
    setSavingTpls(true);
    setTplsMsg("");
    try {
      await api.put("/admin/email-templates", emailTpls);
      setTplsMsg("Templates guardados com sucesso.");
      setTimeout(() => setTplsMsg(""), 3000);
    } catch (er) { setTplsMsg(formatErr(er.response?.data?.detail) || er.message); }
    finally { setSavingTpls(false); }
  };

  // Broadcast attachment helpers
  const addBroadcastAttachment = async (ev) => {
    const filesList = Array.from(ev.target.files || []);
    for (const file of filesList) {
      if (file.size > 10 * 1024 * 1024) { alert(`${file.name}: excede 10 MB`); continue; }
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => { const s = String(r.result || ""); const i = s.indexOf(","); res(i >= 0 ? s.slice(i + 1) : s); };
        r.onerror = () => rej(new Error("read"));
        r.readAsDataURL(file);
      });
      setBroadcast((cur) => ({ ...cur, attachments: [...cur.attachments, { filename: file.name, content_base64: b64, size: file.size }] }));
    }
    ev.target.value = "";
  };
  const removeAttachment = (i) => setBroadcast((cur) => ({ ...cur, attachments: cur.attachments.filter((_, idx) => idx !== i) }));

  const sendBroadcast = async (e) => {
    e.preventDefault();
    setBroadcasting(true);
    setBroadcastMsg("");
    try {
      const payload = {
        subject: broadcast.subject,
        body_html: broadcast.body_html,
        attachments: broadcast.attachments.map((a) => ({ filename: a.filename, content_base64: a.content_base64 })),
        only_approved: true,
      };
      const { data } = await api.post("/admin/broadcast", payload);
      setBroadcastMsg(`Email enviado para ${data.sent} destinatários${data.failed ? ` (${data.failed} falhas)` : ""}.`);
    } catch (er) { setBroadcastMsg(formatErr(er.response?.data?.detail) || er.message); }
    finally { setBroadcasting(false); }
  };

  useEffect(() => {
    // Ensure items always contains subcategories array (backward compat)
    const cs = liveContent.categories_section || {};
    let items = cs.items;
    if (!items || items.length === 0) {
      items = [];
      if (cs.bijuteria_title) items.push({ slug: cs.bijuteria_title, name: cs.bijuteria_title, image_url: cs.bijuteria_image || "", subcategories: [] });
      if (cs.retrosaria_title) items.push({ slug: cs.retrosaria_title, name: cs.retrosaria_title, image_url: cs.retrosaria_image || "", subcategories: [] });
    }
    items = items.map((it) => ({ ...it, subcategories: it.subcategories || [] }));
    setContent({ ...emptyContent, ...liveContent, categories_section: { eyebrow: cs.eyebrow || "", title: cs.title || "", items } });
  }, [liveContent]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => setCategories([]));
  }, [liveContent]);

  const load = async () => {
    const [p, o, s] = await Promise.all([
      api.get("/admin/products"),
      api.get("/orders").catch(() => ({ data: [] })),
      api.get("/settings").catch(() => ({ data: emptySettings })),
    ]);
    setProducts(p.data);
    setOrders(o.data);
    setSettings({ ...emptySettings, ...s.data });
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.image_url) { setErr("Adicione uma imagem ao produto."); return; }
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        price_b2b: form.price_b2b === "" || form.price_b2b === null ? null : parseFloat(form.price_b2b),
        stock: parseInt(form.stock),
        weight_g: form.weight_g === "" || form.weight_g === null ? null : parseFloat(form.weight_g),
      };
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      setEditing(null); setForm(emptyForm); setShowForm(false);
      load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
  };

  const edit = (p) => { setEditing(p.id); setForm({ ...emptyForm, ...p, price: String(p.price), price_b2b: p.price_b2b == null ? "" : String(p.price_b2b), stock: p.stock, weight_g: p.weight_g == null ? "" : String(p.weight_g), subcategory: p.subcategory || "" }); setShowForm(true); };
  const del = async (id) => {
    if (!window.confirm("Eliminar definitivamente este produto? Se estiver associado a encomendas, arquive-o em vez de eliminar.")) return;
    try { await api.delete(`/products/${id}`); load(); }
    catch (e) { alert(formatErr(e.response?.data?.detail) || e.message); }
  };
  const patchProduct = async (p, changes) => {
    try {
      const payload = { ...p, price_b2b: p.price_b2b ?? null, weight_g: p.weight_g ?? null, ...changes };
      await api.put(`/products/${p.id}`, payload);
      load();
    } catch (e) { alert(formatErr(e.response?.data?.detail) || e.message); }
  };
  const toggleFeatured = async (p) => patchProduct(p, { featured: !p.featured });
  const toggleActive = async (p) => patchProduct(p, { active: p.active === false ? true : false });
  const toggleArchived = async (p) => patchProduct(p, { archived: p.archived === true ? false : true });
  const newProduct = () => {
    const firstCat = categories[0]?.slug || "Bijuteria";
    setEditing(null);
    setForm({ ...emptyForm, category: firstCat });
    setShowForm(true);
    setImgErr("");
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgErr("");
    if (file.size > 8 * 1024 * 1024) {
      setImgErr("Imagem demasiado grande (máx. 8 MB).");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToCompressedDataURL(file, 1000, 0.85);
      setForm((cur) => ({ ...cur, image_url: dataUrl }));
    } catch (err) {
      setImgErr(err.message || "Falha ao processar a imagem.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const clearImage = () => setForm((cur) => ({ ...cur, image_url: "" }));

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    setSettingsMsg("");
    try {
      const payload = {
        ...settings,
        shipping_cost: parseFloat(settings.shipping_cost) || 0,
        free_shipping_threshold: parseFloat(settings.free_shipping_threshold) || 0,
        return_policy_days: parseInt(settings.return_policy_days) || 14,
      };
      await api.put("/settings", payload);
      setSettingsMsg("Definições guardadas com sucesso.");
      setTimeout(() => setSettingsMsg(""), 3000);
    } catch (e) {
      setSettingsMsg(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSavingSettings(false); }
  };

  const setS = (k, v) => setSettings((cur) => ({ ...cur, [k]: v }));

  // Content helpers
  const setHero = (k, v) => setContent((c) => ({ ...c, hero: { ...c.hero, [k]: v } }));
  const setCats = (k, v) => setContent((c) => ({ ...c, categories_section: { ...c.categories_section, [k]: v } }));
  const setCatItem = (i, k, v) => setContent((c) => {
    const items = [...(c.categories_section.items || [])];
    items[i] = { ...items[i], [k]: v };
    // Keep slug in sync with name if slug was empty
    if (k === "name" && !items[i].slug) items[i].slug = v;
    return { ...c, categories_section: { ...c.categories_section, items } };
  });
  const addCatItem = () => setContent((c) => ({
    ...c,
    categories_section: { ...c.categories_section, items: [...(c.categories_section.items || []), { slug: "", name: "", image_url: "" }] },
  }));
  const removeCatItem = (i) => setContent((c) => ({
    ...c,
    categories_section: { ...c.categories_section, items: (c.categories_section.items || []).filter((_, idx) => idx !== i) },
  }));
  const moveCatItem = (i, dir) => setContent((c) => {
    const items = [...(c.categories_section.items || [])];
    const j = i + dir;
    if (j < 0 || j >= items.length) return c;
    [items[i], items[j]] = [items[j], items[i]];
    return { ...c, categories_section: { ...c.categories_section, items } };
  });

  // Subcategory helpers
  const setSubItem = (i, j, k, v) => setContent((c) => {
    const items = [...(c.categories_section.items || [])];
    const subs = [...(items[i].subcategories || [])];
    subs[j] = { ...subs[j], [k]: v };
    if (k === "name" && !subs[j].slug) subs[j].slug = v;
    items[i] = { ...items[i], subcategories: subs };
    return { ...c, categories_section: { ...c.categories_section, items } };
  });
  const addSubItem = (i) => setContent((c) => {
    const items = [...(c.categories_section.items || [])];
    items[i] = { ...items[i], subcategories: [...(items[i].subcategories || []), { slug: "", name: "" }] };
    return { ...c, categories_section: { ...c.categories_section, items } };
  });
  const removeSubItem = (i, j) => setContent((c) => {
    const items = [...(c.categories_section.items || [])];
    items[i] = { ...items[i], subcategories: (items[i].subcategories || []).filter((_, idx) => idx !== j) };
    return { ...c, categories_section: { ...c.categories_section, items } };
  });
  const moveSubItem = (i, j, dir) => setContent((c) => {
    const items = [...(c.categories_section.items || [])];
    const subs = [...(items[i].subcategories || [])];
    const k = j + dir;
    if (k < 0 || k >= subs.length) return c;
    [subs[j], subs[k]] = [subs[k], subs[j]];
    items[i] = { ...items[i], subcategories: subs };
    return { ...c, categories_section: { ...c.categories_section, items } };
  });
  const setFeat = (k, v) => setContent((c) => ({ ...c, featured_section: { ...c.featured_section, [k]: v } }));
  const setEdit = (k, v) => setContent((c) => ({ ...c, editorial_cta: { ...c.editorial_cta, [k]: v } }));
  const setBenefit = (i, k, v) => setContent((c) => {
    const next = [...(c.benefits || [])];
    next[i] = { ...next[i], [k]: v };
    return { ...c, benefits: next };
  });
  const setMenuItem = (i, k, v) => setContent((c) => {
    const next = [...(c.nav_menu || [])];
    next[i] = { ...next[i], [k]: v };
    return { ...c, nav_menu: next };
  });
  const addMenuItem = () => setContent((c) => ({ ...c, nav_menu: [...(c.nav_menu || []), { label: "", path: "/" }] }));
  const removeMenuItem = (i) => setContent((c) => ({ ...c, nav_menu: (c.nav_menu || []).filter((_, idx) => idx !== i) }));
  const moveMenuItem = (i, dir) => setContent((c) => {
    const next = [...(c.nav_menu || [])];
    const j = i + dir;
    if (j < 0 || j >= next.length) return c;
    [next[i], next[j]] = [next[j], next[i]];
    return { ...c, nav_menu: next };
  });

  // Registration fields helpers
  const setRegField = (i, k, v) => setContent((c) => {
    const next = [...(c.registration_fields || [])];
    next[i] = { ...next[i], [k]: v };
    return { ...c, registration_fields: next };
  });
  const addRegField = () => setContent((c) => ({
    ...c,
    registration_fields: [...(c.registration_fields || []), { key: "", label: "", type: "text", required: false, placeholder: "" }],
  }));
  const removeRegField = (i) => setContent((c) => ({
    ...c,
    registration_fields: (c.registration_fields || []).filter((_, idx) => idx !== i),
  }));
  const moveRegField = (i, dir) => setContent((c) => {
    const next = [...(c.registration_fields || [])];
    const j = i + dir;
    if (j < 0 || j >= next.length) return c;
    [next[i], next[j]] = [next[j], next[i]];
    return { ...c, registration_fields: next };
  });

  // Header helpers
  const setHeader = (k, v) => setContent((c) => ({ ...c, header: { ...(c.header || {}), [k]: v } }));

  // Footer helpers
  const setFooterField = (k, v) => setContent((c) => ({ ...c, footer: { ...(c.footer || {}), [k]: v } }));
  const setFooterCol = (i, k, v) => setContent((c) => {
    const cols = [...(c.footer?.columns || [])];
    cols[i] = { ...cols[i], [k]: v };
    return { ...c, footer: { ...(c.footer || {}), columns: cols } };
  });
  const setFooterColLink = (colIdx, linkIdx, k, v) => setContent((c) => {
    const cols = [...(c.footer?.columns || [])];
    const links = [...(cols[colIdx]?.links || [])];
    links[linkIdx] = { ...links[linkIdx], [k]: v };
    cols[colIdx] = { ...cols[colIdx], links };
    return { ...c, footer: { ...(c.footer || {}), columns: cols } };
  });
  const addFooterCol = () => setContent((c) => ({
    ...c,
    footer: { ...(c.footer || {}), columns: [...(c.footer?.columns || []), { title: "", links: [] }] },
  }));
  const removeFooterCol = (i) => setContent((c) => ({
    ...c,
    footer: { ...(c.footer || {}), columns: (c.footer?.columns || []).filter((_, idx) => idx !== i) },
  }));
  const addFooterColLink = (colIdx) => setContent((c) => {
    const cols = [...(c.footer?.columns || [])];
    cols[colIdx] = { ...cols[colIdx], links: [...(cols[colIdx]?.links || []), { label: "", url: "" }] };
    return { ...c, footer: { ...(c.footer || {}), columns: cols } };
  });
  const removeFooterColLink = (colIdx, linkIdx) => setContent((c) => {
    const cols = [...(c.footer?.columns || [])];
    cols[colIdx] = { ...cols[colIdx], links: (cols[colIdx]?.links || []).filter((_, idx) => idx !== linkIdx) };
    return { ...c, footer: { ...(c.footer || {}), columns: cols } };
  });

  const saveContent = async (e) => {
    e.preventDefault();
    setSavingContent(true);
    setContentMsg("");
    try {
      await api.put("/content", content);
      await refreshContent();
      setContentMsg("Conteúdo atualizado com sucesso.");
      setTimeout(() => setContentMsg(""), 3000);
    } catch (er) {
      setContentMsg(formatErr(er.response?.data?.detail) || er.message);
    } finally { setSavingContent(false); }
  };

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12" data-testid="page-admin">
      <p className="label-sm mb-3">Administração</p>
      <h1 className="font-serif text-4xl lg:text-5xl brand-text mb-10">Painel Au and Co.</h1>

      <div className="flex gap-6 border-b brand-border mb-8 overflow-x-auto">
        {[
          { k: "products", l: "Produtos" },
          { k: "featured", l: "Destaques" },
          { k: "orders", l: "Encomendas" },
          { k: "registrations", l: "Clientes" },
          { k: "shipping", l: "Portes" },
          { k: "content", l: "Conteúdo" },
          { k: "pages", l: "Páginas" },
          { k: "payments", l: "Pagamentos" },
          { k: "emails", l: "Emails" },
          { k: "broadcast", l: "Newsletter" },
          { k: "settings", l: "Definições" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`py-3 text-sm uppercase tracking-wider whitespace-nowrap ${tab === t.k ? "brand-primary-text border-b-2" : "brand-muted"}`}
            style={tab === t.k ? { borderColor: "#8C6A3D" } : {}}
            data-testid={`tab-${t.k}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div>
          <div className="flex justify-between mb-6">
            <p className="brand-muted text-sm">{products.length} produtos</p>
            <button onClick={newProduct} className="btn-primary" data-testid="btn-new-product">
              <Plus size={16} /> Novo produto
            </button>
          </div>

          {showForm && (
            <form onSubmit={save} className="brand-surface p-6 mb-8 space-y-4" data-testid="product-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input required placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-line" data-testid="pf-name" />
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, subcategory: "" })} className="input-line" data-testid="pf-category">
                  {categories.length === 0 ? (
                    <>
                      <option value="Bijuteria">Bijuteria</option>
                      <option value="Retrosaria">Retrosaria</option>
                    </>
                  ) : (
                    categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)
                  )}
                </select>
                {(() => {
                  const currentCat = categories.find((c) => c.slug === form.category);
                  const subs = currentCat?.subcategories || [];
                  if (subs.length === 0) return null;
                  return (
                    <select value={form.subcategory || ""} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className="input-line" data-testid="pf-subcategory">
                      <option value="">— Sem subcategoria —</option>
                      {subs.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                    </select>
                  );
                })()}
                <input required type="number" step="0.01" placeholder="Preço (EUR)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="input-line" data-testid="pf-price" />
                <input type="number" step="0.01" placeholder="Preço B2B / profissional (opcional)" value={form.price_b2b} onChange={(e) => setForm({ ...form, price_b2b: e.target.value })} className="input-line" data-testid="pf-price-b2b" />
                <input required type="number" placeholder="Stock (apenas visível no painel)" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="input-line" data-testid="pf-stock" />
                <input required type="number" step="0.1" min="0.1" placeholder="Peso (g) — obrigatório para portes" value={form.weight_g} onChange={(e) => setForm({ ...form, weight_g: e.target.value })} className="input-line" data-testid="pf-weight" />

                <div className="md:col-span-2 space-y-3">
                  <label className="label-sm">Imagem do produto</label>
                  {form.image_url && (
                    <div className="relative inline-block">
                      <img src={form.image_url} alt="Pré-visualização" className="w-40 h-48 object-cover brand-border border" data-testid="pf-image-preview" />
                      <button type="button" onClick={clearImage} className="absolute -top-2 -right-2 bg-white rounded-full p-1 brand-border border shadow" aria-label="Remover imagem" data-testid="pf-image-clear">
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3 items-center">
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" data-testid="pf-image-file" />
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="btn-ghost inline-flex items-center gap-2" disabled={uploading} data-testid="pf-image-upload-btn">
                      <Upload size={14} /> {uploading ? "A processar..." : form.image_url ? "Substituir imagem" : "Carregar do computador"}
                    </button>
                    <span className="text-xs brand-muted">ou cole um URL</span>
                  </div>
                  <input
                    placeholder="https://... (URL da imagem)"
                    value={form.image_url?.startsWith("data:") ? "" : form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    className="input-line"
                    data-testid="pf-image"
                  />
                  {imgErr && <p className="text-xs" style={{ color: "#9E4747" }} data-testid="pf-image-err">{imgErr}</p>}
                  {!form.image_url && <p className="text-xs brand-muted">Recomendado: imagens 4:5 (ex. 1000x1250 px). JPG, PNG ou WEBP.</p>}
                </div>
                <textarea required placeholder="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-line md:col-span-2 min-h-[100px]" data-testid="pf-desc" />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="pf-featured" />
                  Em destaque
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="pf-active" />
                  Visível na loja
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.archived === true} onChange={(e) => setForm({ ...form, archived: e.target.checked })} data-testid="pf-archived" />
                  Arquivado
                </label>
              </div>
              {err && <p className="text-sm" style={{ color: "#9E4747" }}>{err}</p>}
              <div className="flex gap-3">
                <button type="submit" className="btn-primary" data-testid="pf-save">{editing ? "Guardar alterações" : "Criar produto"}</button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); setForm(emptyForm); }} className="btn-ghost">Cancelar</button>
              </div>
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="products-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider brand-muted border-b brand-border">
                  <th className="py-3">Produto</th>
                  <th className="py-3">Categoria</th>
                  <th className="py-3">Preço</th>
                  <th className="py-3">Stock</th>
                  <th className="py-3">Peso</th>
                  <th className="py-3">Estado</th>
                  <th className="py-3">Destaque</th>
                  <th className="py-3"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b brand-border" data-testid={`row-${p.id}`}>
                    <td className="py-4 flex items-center gap-3">
                      <img src={p.image_url} alt="" className="w-12 h-14 object-cover" />
                      <span className="text-sm">{p.name}</span>
                    </td>
                    <td className="text-sm">{p.category}{p.subcategory ? ` › ${p.subcategory}` : ""}</td>
                    <td className="text-sm">{eur(p.price)}</td>
                    <td className={`text-sm ${p.stock <= 0 ? "text-red-700 font-medium" : ""}`}>{p.stock <= 0 ? "Esgotado" : p.stock}</td>
                    <td className="text-sm" data-testid={`weight-${p.id}`}>
                      {p.weight_g == null ? (
                        <span style={{ color: "#9E4747" }} title="Peso não definido — necessário para portes">⚠ Não definido</span>
                      ) : (
                        <span>{p.weight_g} g</span>
                      )}
                    </td>
                    <td className="text-sm">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => toggleActive(p)}
                          className="p-1 hover:brand-surface"
                          title={p.active === false ? "Oculto — clique para mostrar" : "Visível — clique para ocultar"}
                          data-testid={`toggle-active-${p.id}`}
                        >
                          {p.active === false ? <EyeOff size={16} className="text-red-700" /> : <Eye size={16} className="text-[#4A6B53]" />}
                        </button>
                        <button
                          onClick={() => toggleArchived(p)}
                          className="p-1 hover:brand-surface"
                          title={p.archived === true ? "Arquivado — clique para desarquivar" : "Arquivar"}
                          data-testid={`toggle-archived-${p.id}`}
                        >
                          <Archive size={16} className={p.archived === true ? "text-[#8C6A3D]" : "brand-muted"} />
                        </button>
                      </div>
                    </td>
                    <td className="text-sm">
                      <button
                        onClick={() => toggleFeatured(p)}
                        className="p-1 hover:brand-surface inline-flex items-center gap-1"
                        aria-label={p.featured ? "Retirar dos destaques" : "Adicionar aos destaques"}
                        title={p.featured ? "Retirar dos destaques" : "Adicionar aos destaques"}
                        data-testid={`toggle-featured-${p.id}`}
                      >
                        {p.featured ? <Star size={16} className="text-[#C9A24A]" fill="#C9A24A" /> : <StarOff size={16} className="brand-muted" />}
                      </button>
                    </td>
                    <td className="text-right">
                      <button onClick={() => edit(p)} className="p-2 hover:brand-surface" data-testid={`btn-edit-${p.id}`}><Pencil size={14} /></button>
                      <button onClick={() => del(p.id)} className="p-2 hover:brand-surface text-red-700" data-testid={`btn-del-${p.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div data-testid="admin-orders">
          {orders.length === 0 ? (
            <p className="brand-muted">Sem encomendas ainda.</p>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => {
                const isPaid = o.payment_status === "paid";
                const method = o.payment_method === "mbway" ? "MB WAY" : o.payment_method === "transfer" ? "Transferência" : (o.payment_method || "—");
                return (
                  <div key={o.id} className="brand-surface p-5 border brand-border" data-testid={`admin-order-${o.id}`}>
                    <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
                      <div>
                        <p className="font-serif text-xl brand-text" data-testid={`order-num-${o.id}`}>#{o.order_number ?? o.id.slice(0, 8)}</p>
                        <p className="text-xs brand-muted">{o.user_email || "Convidado"} · {new Date(o.created_at).toLocaleString("pt-PT")}</p>
                        {o.nif && <p className="text-xs brand-muted mt-1" data-testid={`order-nif-${o.id}`}>NIF: <span className="font-mono">{o.nif}</span></p>}
                      </div>
                      <div className="text-right">
                        <p className="font-serif text-2xl brand-text">{eur(o.amount)}</p>
                        <p className="text-xs brand-muted">Método: {method}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-3 pb-3 border-b brand-border">
                      <div><span className="brand-muted">Subtotal:</span> {eur(o.subtotal ?? (o.amount - (o.shipping_cost || 0)))}</div>
                      <div><span className="brand-muted">Peso:</span> {o.total_weight_g != null ? `${o.total_weight_g} g` : "—"}</div>
                      <div><span className="brand-muted">Portes:</span> {o.shipping_cost != null ? eur(o.shipping_cost) : "—"}</div>
                      <div data-testid={`order-delivery-${o.id}`}>
                        <span className="brand-muted">Entrega:</span> {o.delivery_type === "pickup" ? "Levantamento presencial" : (o.shipping_label || o.shipping_region || "Envio")}
                      </div>
                    </div>

                    <details className="text-xs mb-3">
                      <summary className="cursor-pointer brand-primary-text">Ver produtos ({(o.items || []).length})</summary>
                      <ul className="mt-2 space-y-1">
                        {(o.items || []).map((it, idx) => (
                          <li key={idx} className="flex justify-between">
                            <span>{it.name} × {it.quantity}</span>
                            <span>{eur(it.line_total)}</span>
                          </li>
                        ))}
                      </ul>
                      {o.shipping && (
                        <div className="mt-3 brand-bg p-3 border brand-border">
                          <p className="brand-muted">Envio:</p>
                          <p>{o.shipping.name}, {o.shipping.address}, {o.shipping.postal} {o.shipping.city} · {o.shipping.phone}</p>
                        </div>
                      )}
                    </details>

                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs px-3 py-1 ${isPaid ? "bg-[#4A6B53] text-white" : "brand-border border brand-muted"}`} data-testid={`order-status-${o.id}`}>
                        {isPaid ? "Pago" : "Pagamento pendente"}
                      </span>
                      {isPaid ? (
                        <button onClick={() => togglePayment(o.id, "pending")} className="btn-ghost text-xs" data-testid={`btn-mark-pending-${o.id}`}>Marcar pendente</button>
                      ) : (
                        <button onClick={() => togglePayment(o.id, "paid")} className="btn-primary text-xs" data-testid={`btn-mark-paid-${o.id}`}>Marcar como pago</button>
                      )}
                      <label className="text-xs brand-muted ml-auto flex items-center gap-2">
                        Estado:
                        <select
                          value={o.status || "pagamento_pendente"}
                          onChange={(e) => setOrderStatus(o.id, e.target.value)}
                          className="input-line py-1 text-xs"
                          data-testid={`order-status-select-${o.id}`}
                        >
                          <option value="pagamento_pendente">Pagamento pendente</option>
                          <option value="in_preparation">Em preparação</option>
                          <option value="shipped">Enviada</option>
                          <option value="completed">Concluída</option>
                          <option value="cancelled">Cancelada</option>
                        </select>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ============ PAYMENTS ============ */}
      {tab === "payments" && (
        <form onSubmit={savePaySettings} className="space-y-8 max-w-4xl" data-testid="tab-payments-content">
          <p className="brand-muted text-sm">Configure os métodos de pagamento manual (MB WAY e transferência bancária), os escalões de portes por peso e a mensagem que aparece na página &quot;Informações de pagamento&quot;.</p>

          {/* MB WAY */}
          <section className="brand-surface p-6 border brand-border" data-testid="pay-mbway-section">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-serif text-xl brand-text">MB WAY</h3>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!paySettings.mbway?.active} onChange={(e) => setPaySettings({ ...paySettings, mbway: { ...paySettings.mbway, active: e.target.checked } })} data-testid="pay-mbway-active" />
                Ativo
              </label>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label-sm">Número MB WAY</label>
                <input value={paySettings.mbway?.number || ""} onChange={(e) => setPaySettings({ ...paySettings, mbway: { ...paySettings.mbway, number: e.target.value } })} className="input-line mt-2 font-mono" placeholder="+351 9XX XXX XXX" data-testid="pay-mbway-number" />
              </div>
              <div>
                <label className="label-sm">Instruções para o cliente</label>
                <textarea value={paySettings.mbway?.instructions || ""} onChange={(e) => setPaySettings({ ...paySettings, mbway: { ...paySettings.mbway, instructions: e.target.value } })} className="input-line mt-2 min-h-[80px]" data-testid="pay-mbway-instructions" />
              </div>
            </div>
          </section>

          {/* TRANSFER */}
          <section className="brand-surface p-6 border brand-border" data-testid="pay-transfer-section">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-serif text-xl brand-text">Transferência bancária</h3>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!paySettings.transfer?.active} onChange={(e) => setPaySettings({ ...paySettings, transfer: { ...paySettings.transfer, active: e.target.checked } })} data-testid="pay-transfer-active" />
                Ativo
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label-sm">Titular</label>
                <input value={paySettings.transfer?.holder || ""} onChange={(e) => setPaySettings({ ...paySettings, transfer: { ...paySettings.transfer, holder: e.target.value } })} className="input-line mt-2" data-testid="pay-transfer-holder" />
              </div>
              <div>
                <label className="label-sm">BIC / SWIFT</label>
                <input value={paySettings.transfer?.bic || ""} onChange={(e) => setPaySettings({ ...paySettings, transfer: { ...paySettings.transfer, bic: e.target.value } })} className="input-line mt-2 font-mono" data-testid="pay-transfer-bic" />
              </div>
              <div className="md:col-span-2">
                <label className="label-sm">IBAN</label>
                <input value={paySettings.transfer?.iban || ""} onChange={(e) => setPaySettings({ ...paySettings, transfer: { ...paySettings.transfer, iban: e.target.value } })} className="input-line mt-2 font-mono" placeholder="PT50 0000 0000 0000 0000 0000 0" data-testid="pay-transfer-iban" />
              </div>
              <div className="md:col-span-2">
                <label className="label-sm">Instruções para o cliente</label>
                <textarea value={paySettings.transfer?.instructions || ""} onChange={(e) => setPaySettings({ ...paySettings, transfer: { ...paySettings.transfer, instructions: e.target.value } })} className="input-line mt-2 min-h-[80px]" data-testid="pay-transfer-instructions" />
              </div>
            </div>
          </section>

          {/* PAYMENT PAGE MESSAGE */}
          <section className="brand-surface p-6 border brand-border" data-testid="pay-message-section">
            <p className="text-xs brand-muted mb-4">Os portes são configurados no separador <strong>Portes</strong> (zonas, modalidades e escalões de peso).</p>
            <h3 className="font-serif text-xl brand-text mb-2">Mensagem da página &quot;Informações de pagamento&quot;</h3>
            <p className="text-xs brand-muted mb-4">Texto que aparece ao cliente logo após confirmar a encomenda.</p>
            <textarea value={paySettings.payment_page_message || ""} onChange={(e) => setPaySettings({ ...paySettings, payment_page_message: e.target.value })} className="input-line min-h-[140px]" data-testid="pay-message-textarea" />
          </section>

          {paySettingsMsg && <p className="text-sm" style={{ color: paySettingsMsg.includes("sucesso") ? "#4A6B53" : "#9E4747" }} data-testid="pay-settings-msg">{paySettingsMsg}</p>}

          <div className="flex gap-3 sticky bottom-4 brand-surface p-4 border brand-border">
            <button type="submit" disabled={savingPay} className="btn-primary" data-testid="btn-save-payments">
              <Save size={16} /> {savingPay ? "A guardar..." : "Guardar pagamentos"}
            </button>
          </div>
        </form>
      )}

      {/* ============ PORTES (SHIPPING) ============ */}
      {tab === "shipping" && shipping && (
        <form onSubmit={saveShipping} className="space-y-8 max-w-5xl" data-testid="tab-shipping-content">
          <p className="brand-muted text-sm">
            Gira zonas de envio, modalidades e escalões de peso. Cada escalão só é aplicado se estiver <strong>ativo</strong>.
            Se não existir tarifa válida para o destino/peso, o cliente vê a mensagem &quot;Portes sob consulta&quot;.
            O tarifário do Continente nunca é aplicado a outras zonas.
          </p>

          <section className="brand-surface p-6 border brand-border">
            <label className="label-sm">Mensagem &quot;Portes sob consulta&quot;</label>
            <input value={shipping.quote_message || ""} onChange={(e) => setShipping({ ...shipping, quote_message: e.target.value })} className="input-line mt-2" data-testid="ship-quote-message" />
          </section>

          {/* PICKUP */}
          <section className="brand-surface p-6 border brand-border" data-testid="ship-pickup-section">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-serif text-xl brand-text">Levantamento presencial</h3>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!shipping.pickup?.active} onChange={(e) => setShipping({ ...shipping, pickup: { ...shipping.pickup, active: e.target.checked } })} data-testid="ship-pickup-active" />
                Ativo
              </label>
            </div>
            <div className="space-y-3">
              <input value={shipping.pickup?.name || ""} onChange={(e) => setShipping({ ...shipping, pickup: { ...shipping.pickup, name: e.target.value } })} className="input-line" placeholder="Nome" data-testid="ship-pickup-name" />
              <textarea value={shipping.pickup?.description || ""} onChange={(e) => setShipping({ ...shipping, pickup: { ...shipping.pickup, description: e.target.value } })} className="input-line min-h-[70px]" placeholder="Descrição / instruções" data-testid="ship-pickup-description" />
              <p className="text-xs brand-muted">Valor: 0,00 € · O local não está associado à morada da empresa.</p>
            </div>
          </section>

          {/* ZONES */}
          {(shipping.zones || []).map((z, zi) => (
            <section key={z.id} className="brand-surface p-6 border brand-border" data-testid={`ship-zone-${z.region}`}>
              <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <input value={z.name} onChange={(e) => mutShip((c) => { c.zones[zi].name = e.target.value; })} className="input-line font-serif text-lg" data-testid={`zone-name-${zi}`} />
                  <span className="text-xs uppercase tracking-wider brand-muted">{z.region}</span>
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={z.active !== false} onChange={(e) => mutShip((c) => { c.zones[zi].active = e.target.checked; })} data-testid={`zone-active-${zi}`} />
                    Ativa
                  </label>
                  {!["continental", "azores", "madeira"].includes(z.region) && (
                    <button type="button" onClick={() => { if (window.confirm("Remover esta zona?")) mutShip((c) => c.zones.splice(zi, 1)); }} className="p-2 text-red-700" title="Remover zona"><Trash2 size={14} /></button>
                  )}
                </div>
              </div>

              {z.region === "international" && (
                <div className="mb-4">
                  <label className="label-sm">Países / grupos (separados por vírgula, ex.: ES, FR, DE)</label>
                  <input
                    value={(z.countries || []).join(", ")}
                    onChange={(e) => mutShip((c) => { c.zones[zi].countries = e.target.value.split(",").map((s) => s.trim()).filter(Boolean); })}
                    className="input-line mt-2"
                    placeholder="Deixe vazio para aplicar a todos os países"
                    data-testid={`zone-countries-${zi}`}
                  />
                </div>
              )}

              <div className="space-y-4">
                {(z.modalities || []).map((m, mi) => (
                  <div key={m.id} className="brand-bg p-4 border brand-border" data-testid={`modality-${zi}-${mi}`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
                      <input value={m.name} onChange={(e) => mutShip((c) => { c.zones[zi].modalities[mi].name = e.target.value; })} className="input-line" placeholder="Modalidade (ex.: 72h)" data-testid={`mod-name-${zi}-${mi}`} />
                      <input value={m.carrier || ""} onChange={(e) => mutShip((c) => { c.zones[zi].modalities[mi].carrier = e.target.value; })} className="input-line" placeholder="Transportadora" data-testid={`mod-carrier-${zi}-${mi}`} />
                      <input value={m.delivery_time || ""} onChange={(e) => mutShip((c) => { c.zones[zi].modalities[mi].delivery_time = e.target.value; })} className="input-line" placeholder="Prazo (ex.: 2-3 dias)" data-testid={`mod-time-${zi}-${mi}`} />
                    </div>
                    <div className="flex items-center gap-4 mb-3">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={m.active !== false} onChange={(e) => mutShip((c) => { c.zones[zi].modalities[mi].active = e.target.checked; })} data-testid={`mod-active-${zi}-${mi}`} />
                        Ativa
                      </label>
                      <input value={m.message || ""} onChange={(e) => mutShip((c) => { c.zones[zi].modalities[mi].message = e.target.value; })} className="input-line flex-1" placeholder="Mensagem informativa (opcional)" />
                      <button type="button" onClick={() => mutShip((c) => c.zones[zi].modalities.splice(mi, 1))} className="p-2 text-red-700" title="Remover modalidade"><Trash2 size={14} /></button>
                    </div>

                    <p className="label-sm mb-2">Escalões de peso</p>
                    <div className="space-y-2">
                      {(m.tiers || []).map((t, ti) => (
                        <div key={t.id} className="grid grid-cols-2 md:grid-cols-[1fr_1fr_1fr_auto_auto] gap-2 items-center" data-testid={`tier-${zi}-${mi}-${ti}`}>
                          <input type="number" step="1" value={t.min_g ?? 0} onChange={(e) => mutShip((c) => { c.zones[zi].modalities[mi].tiers[ti].min_g = parseFloat(e.target.value) || 0; })} className="input-line" placeholder="Peso mín (g)" data-testid={`tier-min-${zi}-${mi}-${ti}`} />
                          <input type="number" step="1" value={t.max_g ?? ""} onChange={(e) => mutShip((c) => { c.zones[zi].modalities[mi].tiers[ti].max_g = e.target.value === "" ? null : parseFloat(e.target.value); })} className="input-line" placeholder="Peso máx (g) — vazio = sem limite" data-testid={`tier-max-${zi}-${mi}-${ti}`} />
                          <input type="number" step="0.01" value={t.price ?? 0} onChange={(e) => mutShip((c) => { c.zones[zi].modalities[mi].tiers[ti].price = parseFloat(e.target.value) || 0; })} className="input-line" placeholder="Preço final c/ IVA (€)" data-testid={`tier-price-${zi}-${mi}-${ti}`} />
                          <label className="flex items-center gap-1 text-xs">
                            <input type="checkbox" checked={t.active !== false} onChange={(e) => mutShip((c) => { c.zones[zi].modalities[mi].tiers[ti].active = e.target.checked; })} data-testid={`tier-active-${zi}-${mi}-${ti}`} />
                            Ativo
                          </label>
                          <button type="button" onClick={() => mutShip((c) => c.zones[zi].modalities[mi].tiers.splice(ti, 1))} className="p-2 text-red-700"><Trash2 size={14} /></button>
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={() => addTier(zi, mi)} className="btn-ghost mt-3 inline-flex items-center gap-2 text-xs" data-testid={`add-tier-${zi}-${mi}`}><Plus size={12} /> Adicionar escalão</button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => addModality(zi)} className="btn-ghost mt-4 inline-flex items-center gap-2" data-testid={`add-modality-${zi}`}><Plus size={14} /> Adicionar modalidade</button>
            </section>
          ))}

          <button type="button" onClick={addZone} className="btn-ghost inline-flex items-center gap-2" data-testid="add-zone"><Plus size={14} /> Adicionar zona internacional</button>

          {shipMsg && <p className="text-sm" style={{ color: shipMsg.includes("sucesso") ? "#4A6B53" : "#9E4747" }} data-testid="ship-msg">{shipMsg}</p>}
          <div className="flex gap-3 sticky bottom-4 brand-surface p-4 border brand-border">
            <button type="submit" disabled={savingShip} className="btn-primary" data-testid="btn-save-shipping"><Save size={16} /> {savingShip ? "A guardar..." : "Guardar portes"}</button>
          </div>
        </form>
      )}

      {/* ============ PÁGINAS (CMS) ============ */}
      {tab === "pages" && (
        <div data-testid="tab-pages-content">
          {!pageForm ? (
            <>
              <div className="flex justify-between mb-6">
                <p className="brand-muted text-sm">{pages.length} páginas</p>
                <button onClick={() => setPageForm({ slug: "", title: "", body_html: "", image_url: "", published: true, seo_title: "", seo_description: "" })} className="btn-primary" data-testid="btn-new-page"><Plus size={16} /> Nova página</button>
              </div>
              <div className="space-y-3">
                {pages.map((pg) => (
                  <div key={pg.id} className="brand-surface p-4 border brand-border flex justify-between items-center" data-testid={`page-row-${pg.slug}`}>
                    <div>
                      <p className="brand-text font-medium">{pg.title}</p>
                      <p className="text-xs brand-muted">/pagina/{pg.slug} · {pg.published ? "Publicada" : "Rascunho"}</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setPageForm({ ...pg })} className="p-2 hover:brand-bg" data-testid={`btn-edit-page-${pg.slug}`}><Pencil size={14} /></button>
                      <button onClick={() => deletePage(pg.id)} className="p-2 text-red-700 hover:brand-bg" data-testid={`btn-del-page-${pg.slug}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <form onSubmit={savePage} className="space-y-4 max-w-3xl" data-testid="page-form">
              <input required value={pageForm.title} onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })} className="input-line" placeholder="Título" data-testid="page-title" />
              <input value={pageForm.slug} onChange={(e) => setPageForm({ ...pageForm, slug: e.target.value })} className="input-line font-mono" placeholder="slug (ex.: envios-devolucoes) — deixe vazio para gerar do título" data-testid="page-slug" />
              <input value={pageForm.image_url || ""} onChange={(e) => setPageForm({ ...pageForm, image_url: e.target.value })} className="input-line" placeholder="URL da imagem (opcional)" data-testid="page-image" />
              <textarea required value={pageForm.body_html} onChange={(e) => setPageForm({ ...pageForm, body_html: e.target.value })} className="input-line min-h-[240px] font-mono text-sm" placeholder="Conteúdo (HTML permitido: <p>, <strong>, <ul>, <li>, ...)" data-testid="page-body" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input value={pageForm.seo_title || ""} onChange={(e) => setPageForm({ ...pageForm, seo_title: e.target.value })} className="input-line" placeholder="SEO: meta title (opcional)" data-testid="page-seo-title" />
                <input value={pageForm.seo_description || ""} onChange={(e) => setPageForm({ ...pageForm, seo_description: e.target.value })} className="input-line" placeholder="SEO: meta description (opcional)" data-testid="page-seo-desc" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={pageForm.published !== false} onChange={(e) => setPageForm({ ...pageForm, published: e.target.checked })} data-testid="page-published" />
                Publicada
              </label>
              {pageMsg && <p className="text-sm" style={{ color: "#9E4747" }} data-testid="page-msg">{pageMsg}</p>}
              <div className="flex gap-3">
                <button type="submit" className="btn-primary" data-testid="btn-save-page"><Save size={16} /> Guardar página</button>
                <button type="button" onClick={() => { setPageForm(null); setPageMsg(""); }} className="btn-ghost">Cancelar</button>
              </div>
            </form>
          )}
        </div>
      )}


      {tab === "content" && (
        <form onSubmit={saveContent} className="space-y-10 max-w-5xl" data-testid="content-form">
          {/* ANNOUNCEMENT BAR */}
          <section className="brand-surface p-6 lg:p-8" data-testid="content-announcement">
            <div className="flex justify-between items-center mb-2">
              <h2 className="font-serif text-2xl brand-text">Barra de aviso (topo)</h2>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!content.announcement?.active} onChange={(e) => setContent({ ...content, announcement: { ...(content.announcement || {}), active: e.target.checked } })} data-testid="ann-active" />
                Ativa
              </label>
            </div>
            <p className="text-sm brand-muted mb-4">Mensagem fina apresentada no topo de todas as páginas (ex.: promoções, avisos).</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input value={content.announcement?.text || ""} onChange={(e) => setContent({ ...content, announcement: { ...(content.announcement || {}), text: e.target.value } })} className="input-line" placeholder="Texto do aviso" data-testid="ann-text" />
              <input value={content.announcement?.link || ""} onChange={(e) => setContent({ ...content, announcement: { ...(content.announcement || {}), link: e.target.value } })} className="input-line" placeholder="Link (opcional, ex.: /loja)" data-testid="ann-link" />
            </div>
          </section>

          {/* HERO */}
          <section className="brand-surface p-6 lg:p-8">
            <h2 className="font-serif text-2xl brand-text mb-2">Banner principal (Hero)</h2>
            <p className="text-sm brand-muted mb-6">Primeira imagem e mensagem que o visitante vê na página inicial.</p>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-6">
              <div className="space-y-3">
                <input placeholder="Pré-título (ex. Bijuteria & Retrosaria)" value={content.hero.eyebrow} onChange={(e) => setHero("eyebrow", e.target.value)} className="input-line" data-testid="hero-eyebrow" />
                <input placeholder="Título — 1ª linha" value={content.hero.title_line1} onChange={(e) => setHero("title_line1", e.target.value)} className="input-line" data-testid="hero-title1" />
                <input placeholder="Título — 2ª linha (itálico)" value={content.hero.title_line2} onChange={(e) => setHero("title_line2", e.target.value)} className="input-line" data-testid="hero-title2" />
                <textarea placeholder="Descrição" value={content.hero.description} onChange={(e) => setHero("description", e.target.value)} className="input-line min-h-[80px]" data-testid="hero-desc" />
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="Botão principal" value={content.hero.cta_primary} onChange={(e) => setHero("cta_primary", e.target.value)} className="input-line" data-testid="hero-cta1" />
                  <input placeholder="Botão secundário" value={content.hero.cta_secondary} onChange={(e) => setHero("cta_secondary", e.target.value)} className="input-line" data-testid="hero-cta2" />
                </div>
              </div>
              <ImageField value={content.hero.image_url} onChange={(v) => setHero("image_url", v)} label="Imagem de fundo" testid="hero-image" aspect="aspect-[3/4]" />
            </div>
          </section>

          {/* BENEFITS */}
          <section className="brand-surface p-6 lg:p-8">
            <h2 className="font-serif text-2xl brand-text mb-2">Vantagens (3 cartões)</h2>
            <p className="text-sm brand-muted mb-6">Aparecem por baixo do banner. Deixe em branco para esconder essa secção.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="brand-bg p-4 border brand-border space-y-2">
                  <p className="label-sm">Vantagem {i + 1}</p>
                  <input placeholder="Título" value={content.benefits[i]?.title || ""} onChange={(e) => setBenefit(i, "title", e.target.value)} className="input-line" data-testid={`ben-title-${i}`} />
                  <textarea placeholder="Texto curto" value={content.benefits[i]?.text || ""} onChange={(e) => setBenefit(i, "text", e.target.value)} className="input-line min-h-[60px]" data-testid={`ben-text-${i}`} />
                </div>
              ))}
            </div>
          </section>

          {/* CATEGORIES SECTION */}
          <section className="brand-surface p-6 lg:p-8">
            <h2 className="font-serif text-2xl brand-text mb-2">Secção de categorias</h2>
            <p className="text-sm brand-muted mb-6">Adicione as categorias que quer mostrar na página inicial e na loja. Cada categoria fica clicável e filtra os produtos automaticamente.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <input placeholder="Pré-título (ex. Categorias)" value={content.categories_section.eyebrow} onChange={(e) => setCats("eyebrow", e.target.value)} className="input-line" data-testid="cats-eyebrow" />
              <input placeholder="Título da secção" value={content.categories_section.title} onChange={(e) => setCats("title", e.target.value)} className="input-line" data-testid="cats-title" />
            </div>

            <div className="space-y-4" data-testid="cats-items-list">
              {(content.categories_section.items || []).length === 0 ? (
                <p className="text-sm brand-muted py-4">Sem categorias. Adicione a primeira em baixo.</p>
              ) : (
                content.categories_section.items.map((it, i) => (
                  <div key={i} className="brand-bg p-4 border brand-border" data-testid={`cat-item-${i}`}>
                    <div className="flex justify-between items-center mb-3">
                      <p className="label-sm">Categoria {i + 1}</p>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => moveCatItem(i, -1)} className="btn-ghost px-2 py-1 text-xs" data-testid={`cat-up-${i}`}>↑</button>
                        <button type="button" onClick={() => moveCatItem(i, 1)} className="btn-ghost px-2 py-1 text-xs" data-testid={`cat-down-${i}`}>↓</button>
                        <button type="button" onClick={() => removeCatItem(i)} className="p-2 text-red-700 hover:brand-surface" aria-label="Remover" data-testid={`cat-remove-${i}`}><Trash2 size={14} /></button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_180px] gap-4">
                      <div className="space-y-3">
                        <div>
                          <label className="label-sm">Nome (aparece no site)</label>
                          <input value={it.name} onChange={(e) => setCatItem(i, "name", e.target.value)} className="input-line mt-2" placeholder="ex. Bijuteria, Retrosaria, Novidades" data-testid={`cat-name-${i}`} />
                        </div>
                        <div>
                          <label className="label-sm">Identificador (slug) — usado nos filtros</label>
                          <input value={it.slug} onChange={(e) => setCatItem(i, "slug", e.target.value)} className="input-line mt-2 font-mono" placeholder="ex. Bijuteria" data-testid={`cat-slug-${i}`} />
                          <p className="text-xs brand-muted mt-1">Se deixar em branco, usa o Nome. Deve corresponder à categoria escolhida nos produtos.</p>
                        </div>
                      </div>
                      <ImageField value={it.image_url} onChange={(v) => setCatItem(i, "image_url", v)} label="Imagem" testid={`cat-image-${i}`} aspect="aspect-[5/4]" />
                    </div>

                    {/* Subcategories */}
                    <div className="mt-4 pt-4 border-t brand-border" data-testid={`subs-${i}`}>
                      <div className="flex justify-between items-center mb-3">
                        <p className="label-sm">Subcategorias</p>
                        <p className="text-xs brand-muted">Ligar como acesso rápido: <code className="text-xs">/loja?category={it.slug || it.name || "..."}&amp;subcategory=NomeSub</code></p>
                      </div>
                      <div className="space-y-2">
                        {(it.subcategories || []).length === 0 ? (
                          <p className="text-xs brand-muted italic">Sem subcategorias.</p>
                        ) : (
                          it.subcategories.map((s, j) => (
                            <div key={j} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto_auto] gap-2 items-center" data-testid={`sub-row-${i}-${j}`}>
                              <input placeholder="Nome (ex. Anéis)" value={s.name || ""} onChange={(e) => setSubItem(i, j, "name", e.target.value)} className="input-line" data-testid={`sub-name-${i}-${j}`} />
                              <input placeholder="Identificador (opcional)" value={s.slug || ""} onChange={(e) => setSubItem(i, j, "slug", e.target.value)} className="input-line font-mono text-sm" data-testid={`sub-slug-${i}-${j}`} />
                              <button type="button" onClick={() => moveSubItem(i, j, -1)} className="btn-ghost px-2 py-1 text-xs">↑</button>
                              <button type="button" onClick={() => moveSubItem(i, j, 1)} className="btn-ghost px-2 py-1 text-xs">↓</button>
                              <button type="button" onClick={() => removeSubItem(i, j)} className="p-2 text-red-700 hover:brand-surface" aria-label="Remover subcategoria" data-testid={`sub-remove-${i}-${j}`}><Trash2 size={14} /></button>
                            </div>
                          ))
                        )}
                      </div>
                      <button type="button" onClick={() => addSubItem(i)} className="btn-ghost mt-3 text-xs inline-flex items-center gap-2" data-testid={`sub-add-${i}`}>
                        <Plus size={12} /> Adicionar subcategoria
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <button type="button" onClick={addCatItem} className="btn-ghost mt-4 inline-flex items-center gap-2" data-testid="cat-add">
              <Plus size={14} /> Adicionar categoria
            </button>
          </section>

          {/* FEATURED + EDITORIAL */}
          <section className="brand-surface p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h3 className="font-serif text-xl brand-text">Secção &quot;Em destaque&quot;</h3>
              <input placeholder="Pré-título" value={content.featured_section.eyebrow} onChange={(e) => setFeat("eyebrow", e.target.value)} className="input-line" data-testid="feat-eyebrow" />
              <input placeholder="Título" value={content.featured_section.title} onChange={(e) => setFeat("title", e.target.value)} className="input-line" data-testid="feat-title" />
            </div>
            <div className="space-y-3">
              <h3 className="font-serif text-xl brand-text">Bloco editorial (CTA)</h3>
              <input placeholder="Pré-título" value={content.editorial_cta.eyebrow} onChange={(e) => setEdit("eyebrow", e.target.value)} className="input-line" data-testid="edit-eyebrow" />
              <input placeholder="Título" value={content.editorial_cta.title} onChange={(e) => setEdit("title", e.target.value)} className="input-line" data-testid="edit-title" />
              <textarea placeholder="Descrição" value={content.editorial_cta.description} onChange={(e) => setEdit("description", e.target.value)} className="input-line min-h-[60px]" data-testid="edit-desc" />
              <input placeholder="Texto do botão" value={content.editorial_cta.cta_text} onChange={(e) => setEdit("cta_text", e.target.value)} className="input-line" data-testid="edit-cta" />
            </div>
          </section>

          {/* MENU */}
          <section className="brand-surface p-6 lg:p-8">
            <h2 className="font-serif text-2xl brand-text mb-2">Menu de navegação</h2>
            <p className="text-sm brand-muted mb-6">Itens que aparecem no topo do site. Use caminhos relativos (ex. <code className="text-xs">/loja</code>, <code className="text-xs">/loja?category=Bijuteria</code>).</p>
            <div className="space-y-3" data-testid="menu-list">
              {(content.nav_menu || []).map((m, i) => (
                <div key={i} className="grid grid-cols-[24px_1fr_1.4fr_auto_auto_auto] gap-2 items-center" data-testid={`menu-row-${i}`}>
                  <GripVertical size={14} className="brand-muted" />
                  <input placeholder="Etiqueta (ex. Loja)" value={m.label} onChange={(e) => setMenuItem(i, "label", e.target.value)} className="input-line" data-testid={`menu-label-${i}`} />
                  <input placeholder="Caminho (ex. /loja)" value={m.path} onChange={(e) => setMenuItem(i, "path", e.target.value)} className="input-line font-mono" data-testid={`menu-path-${i}`} />
                  <button type="button" onClick={() => moveMenuItem(i, -1)} className="btn-ghost px-2 py-1 text-xs" data-testid={`menu-up-${i}`}>↑</button>
                  <button type="button" onClick={() => moveMenuItem(i, 1)} className="btn-ghost px-2 py-1 text-xs" data-testid={`menu-down-${i}`}>↓</button>
                  <button type="button" onClick={() => removeMenuItem(i)} className="p-2 text-red-700 hover:brand-bg" aria-label="Remover" data-testid={`menu-remove-${i}`}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addMenuItem} className="btn-ghost mt-4 inline-flex items-center gap-2" data-testid="menu-add">
              <Plus size={14} /> Adicionar item ao menu
            </button>
          </section>

          {/* REGISTRATION FIELDS */}
          <section className="brand-surface p-6 lg:p-8">
            <h2 className="font-serif text-2xl brand-text mb-2">Formulário de registo</h2>
            <p className="text-sm brand-muted mb-2">
              Os campos <strong>Nome, Email e Palavra-passe</strong> aparecem sempre. Aqui pode adicionar campos extra (ex. Telefone, Morada, NIF) e marcá-los como obrigatórios. As alterações ficam ativas no site imediatamente após guardar.
            </p>
            <div className="space-y-3 mt-6" data-testid="regfields-list">
              {(content.registration_fields || []).length === 0 ? (
                <p className="text-sm brand-muted py-4">Sem campos extra. Use o botão abaixo para adicionar.</p>
              ) : (
                (content.registration_fields || []).map((f, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.2fr_140px_140px_auto_auto_auto] gap-2 items-center brand-bg p-3 border brand-border" data-testid={`regfield-row-${i}`}>
                    <input placeholder="Chave (ex. phone)" value={f.key} onChange={(e) => setRegField(i, "key", e.target.value.replace(/\s+/g, "_").toLowerCase())} className="input-line font-mono text-sm" data-testid={`regfield-key-${i}`} />
                    <input placeholder="Etiqueta (ex. Telefone)" value={f.label} onChange={(e) => setRegField(i, "label", e.target.value)} className="input-line" data-testid={`regfield-label-${i}`} />
                    <input placeholder="Placeholder" value={f.placeholder || ""} onChange={(e) => setRegField(i, "placeholder", e.target.value)} className="input-line" data-testid={`regfield-ph-${i}`} />
                    <select value={f.type || "text"} onChange={(e) => setRegField(i, "type", e.target.value)} className="input-line" data-testid={`regfield-type-${i}`}>
                      <option value="text">Texto</option>
                      <option value="tel">Telefone</option>
                      <option value="email">Email</option>
                      <option value="number">Número</option>
                      <option value="date">Data</option>
                      <option value="url">URL</option>
                      <option value="file_pdf">Ficheiro PDF</option>
                    </select>
                    <label className="flex items-center gap-2 text-xs brand-text">
                      <input type="checkbox" checked={!!f.required} onChange={(e) => setRegField(i, "required", e.target.checked)} data-testid={`regfield-req-${i}`} />
                      Obrigatório
                    </label>
                    <button type="button" onClick={() => moveRegField(i, -1)} className="btn-ghost px-2 py-1 text-xs" data-testid={`regfield-up-${i}`}>↑</button>
                    <button type="button" onClick={() => moveRegField(i, 1)} className="btn-ghost px-2 py-1 text-xs" data-testid={`regfield-down-${i}`}>↓</button>
                    <button type="button" onClick={() => removeRegField(i)} className="p-2 text-red-700 hover:brand-bg" aria-label="Remover" data-testid={`regfield-remove-${i}`}><Trash2 size={14} /></button>
                  </div>
                ))
              )}
            </div>
            <button type="button" onClick={addRegField} className="btn-ghost mt-4 inline-flex items-center gap-2" data-testid="regfield-add">
              <Plus size={14} /> Adicionar campo de registo
            </button>
            <p className="text-xs brand-muted mt-3">
              <strong>Chave</strong>: identificador único do campo (sem espaços, ex. <code>phone</code>, <code>nif</code>, <code>birth_date</code>). <strong>Etiqueta</strong>: o que o cliente vê acima do campo.
            </p>
          </section>

          {/* HEADER / FOOTER */}
          <section className="brand-surface p-6 lg:p-8">
            <h2 className="font-serif text-2xl brand-text mb-2">Cabeçalho</h2>
            <p className="text-sm brand-muted mb-4">Nome/logotipo que aparece no topo do site.</p>
            <div>
              <label className="label-sm">Nome do logotipo</label>
              <input value={content.header?.logo_text || ""} onChange={(e) => setHeader("logo_text", e.target.value)} className="input-line mt-2 max-w-md" data-testid="hdr-logo" />
            </div>
            <p className="text-xs brand-muted mt-3">Os itens do menu de navegação são editados na secção &quot;Menu de navegação&quot; acima.</p>
          </section>

          <section className="brand-surface p-6 lg:p-8">
            <div className="flex justify-between items-center mb-2">
              <h2 className="font-serif text-2xl brand-text">Rodapé</h2>
            </div>
            <p className="text-sm brand-muted mb-6">Colunas de links no rodapé (Contacto é gerado automaticamente a partir das Definições da loja).</p>

            <div className="space-y-4" data-testid="footer-cols">
              {(content.footer?.columns || []).map((col, ci) => (
                <div key={ci} className="brand-bg p-4 border brand-border" data-testid={`footer-col-${ci}`}>
                  <div className="flex justify-between items-center mb-3">
                    <input placeholder="Título da coluna" value={col.title} onChange={(e) => setFooterCol(ci, "title", e.target.value)} className="input-line max-w-sm" data-testid={`footer-col-title-${ci}`} />
                    <button type="button" onClick={() => removeFooterCol(ci)} className="p-2 text-red-700 hover:brand-surface" aria-label="Remover coluna"><Trash2 size={14} /></button>
                  </div>
                  <div className="space-y-2">
                    {(col.links || []).map((lnk, li) => (
                      <div key={li} className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr_auto] gap-2" data-testid={`footer-link-${ci}-${li}`}>
                        <input placeholder="Etiqueta" value={lnk.label} onChange={(e) => setFooterColLink(ci, li, "label", e.target.value)} className="input-line" />
                        <input placeholder="Link (ex. /loja ou https://...)" value={lnk.url} onChange={(e) => setFooterColLink(ci, li, "url", e.target.value)} className="input-line font-mono text-sm" />
                        <button type="button" onClick={() => removeFooterColLink(ci, li)} className="p-2 text-red-700 hover:brand-bg" aria-label="Remover link"><Trash2 size={14} /></button>
                      </div>
                    ))}
                    <button type="button" onClick={() => addFooterColLink(ci)} className="btn-ghost text-xs inline-flex items-center gap-2">
                      <Plus size={12} /> Adicionar link
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" onClick={addFooterCol} className="btn-ghost mt-4 inline-flex items-center gap-2" data-testid="footer-add-col">
              <Plus size={14} /> Adicionar coluna
            </button>

            <div className="mt-6">
              <label className="label-sm">Linha inferior (métodos de pagamento)</label>
              <input value={content.footer?.payment_line || ""} onChange={(e) => setFooterField("payment_line", e.target.value)} className="input-line mt-2" data-testid="footer-payment-line" />
            </div>
          </section>

          {contentMsg && (
            <p className="text-sm" style={{ color: contentMsg.includes("sucesso") ? "#4A6B53" : "#9E4747" }} data-testid="content-msg">{contentMsg}</p>
          )}

          <div className="flex gap-3 sticky bottom-4 brand-surface p-4 border brand-border">
            <button type="submit" disabled={savingContent} className="btn-primary" data-testid="btn-save-content">
              <Save size={16} /> {savingContent ? "A guardar..." : "Guardar conteúdo"}
            </button>
          </div>
        </form>
      )}

      {/* ============ FEATURED ============ */}
      {tab === "featured" && (
        <div data-testid="tab-featured-content">
          <p className="brand-muted text-sm mb-6">Escolha os produtos que aparecem na secção &quot;Em destaque&quot; da página inicial. Clique na estrela para adicionar ou remover.</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="font-serif text-xl brand-text mb-4">Em destaque ({products.filter((p) => p.featured).length})</h3>
              <div className="space-y-2" data-testid="featured-list">
                {products.filter((p) => p.featured).length === 0 ? (
                  <p className="text-sm brand-muted">Sem produtos em destaque.</p>
                ) : products.filter((p) => p.featured).map((p) => (
                  <div key={p.id} className="brand-surface p-3 flex items-center gap-3" data-testid={`fav-item-${p.id}`}>
                    <img src={p.image_url} alt="" className="w-12 h-14 object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm brand-text truncate">{p.name}</p>
                      <p className="text-xs brand-muted">{eur(p.price)} · {p.category}</p>
                    </div>
                    <button onClick={() => toggleFeatured(p)} className="p-2 hover:brand-bg" title="Retirar dos destaques" data-testid={`fav-remove-${p.id}`}>
                      <Star size={18} className="text-[#C9A24A]" fill="#C9A24A" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-serif text-xl brand-text mb-4">Outros produtos</h3>
              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                {products.filter((p) => !p.featured).length === 0 ? (
                  <p className="text-sm brand-muted">Todos os produtos já estão em destaque.</p>
                ) : products.filter((p) => !p.featured).map((p) => (
                  <div key={p.id} className="brand-surface p-3 flex items-center gap-3" data-testid={`nonfav-item-${p.id}`}>
                    <img src={p.image_url} alt="" className="w-12 h-14 object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm brand-text truncate">{p.name}</p>
                      <p className="text-xs brand-muted">{eur(p.price)} · {p.category}</p>
                    </div>
                    <button onClick={() => toggleFeatured(p)} className="p-2 hover:brand-bg" title="Adicionar aos destaques" data-testid={`fav-add-${p.id}`}>
                      <StarOff size={18} className="brand-muted" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ REGISTRATIONS ============ */}
      {tab === "registrations" && (
        <div data-testid="tab-registrations-content">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <p className="brand-muted text-sm">Estado:</p>
            {["pending", "approved", "disabled", "rejected", ""].map((s) => (
              <button
                key={s || "all"}
                onClick={() => setRegStatusFilter(s)}
                className={`text-xs uppercase tracking-wider px-3 py-1 border ${regStatusFilter === s ? "brand-primary" : "brand-border"}`}
                data-testid={`reg-filter-${s || "all"}`}
              >
                {s === "pending" ? "Pendentes" : s === "approved" ? "Aprovados" : s === "disabled" ? "Desativados" : s === "rejected" ? "Recusados" : "Todos"}
              </button>
            ))}
          </div>

          {registrations.length === 0 ? (
            <p className="brand-muted py-12 text-center">Nenhum pedido nesta categoria.</p>
          ) : (
            <div className="space-y-3">
              {registrations.map((u) => (
                <div key={u.id} className="brand-surface p-5 border brand-border" data-testid={`reg-${u.id}`}>
                  <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
                    <div>
                      <p className="font-serif text-xl brand-text">{u.name}</p>
                      <p className="text-sm brand-muted">{u.email}</p>
                      <p className="text-xs brand-muted mt-1">{new Date(u.created_at).toLocaleString("pt-PT")}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`text-xs px-2 py-1 ${u.approval_status === "approved" ? "bg-[#4A6B53] text-white" : u.approval_status === "rejected" ? "bg-[#9E4747] text-white" : u.approval_status === "disabled" ? "bg-[#7A756D] text-white" : "brand-border border brand-muted"}`}>
                        {u.approval_status === "pending" ? "Pendente" : u.approval_status === "approved" ? "Aprovado" : u.approval_status === "disabled" ? "Desativado" : "Recusado"}
                      </span>
                      {u.role !== "admin" && (
                        <span className="text-xs brand-muted" data-testid={`reg-role-${u.id}`}>{u.role === "b2b" ? "B2B / Profissional" : "B2C / Particular"}</span>
                      )}
                    </div>
                  </div>

                  {Object.keys(u.extra || {}).length > 0 && (
                    <div className="mb-3 text-sm">
                      {Object.entries(u.extra).map(([k, v]) => (
                        <p key={k}><span className="brand-muted">{k}:</span> {v}</p>
                      ))}
                    </div>
                  )}

                  {Object.keys(u.files || {}).length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2" data-testid={`reg-files-${u.id}`}>
                      {Object.entries(u.files).map(([k, meta]) => (
                        <button
                          key={k}
                          onClick={() => downloadUserFile(u.id, k, meta.filename)}
                          className="brand-bg border brand-border px-3 py-2 flex items-center gap-2 text-xs hover:brand-primary hover:text-white"
                          data-testid={`reg-file-${u.id}-${k}`}
                        >
                          <FileText size={14} /> {meta.filename || k} <Download size={12} />
                        </button>
                      ))}
                    </div>
                  )}

                  {u.reviewed_reason && (
                    <p className="text-xs brand-muted italic mb-2">Motivo: {u.reviewed_reason}</p>
                  )}

                  {u.approval_status === "pending" && (
                    <div className="flex gap-2 pt-2 border-t brand-border">
                      <button
                        onClick={() => approveReg(u)}
                        disabled={!!regBusy[u.id]}
                        className="btn-primary text-xs inline-flex items-center gap-2"
                        data-testid={`reg-approve-${u.id}`}
                      >
                        <CheckCircle2 size={14} /> Aprovar
                      </button>
                      <button
                        onClick={() => rejectReg(u)}
                        disabled={!!regBusy[u.id]}
                        className="btn-ghost text-xs inline-flex items-center gap-2"
                        style={{ borderColor: "#9E4747", color: "#9E4747" }}
                        data-testid={`reg-reject-${u.id}`}
                      >
                        <XCircle size={14} /> Recusar
                      </button>
                    </div>
                  )}

                  {u.role !== "admin" && ["approved", "disabled"].includes(u.approval_status) && (
                    <div className="flex flex-wrap gap-2 pt-2 border-t brand-border items-center">
                      {u.approval_status === "approved" ? (
                        <button onClick={() => setUserStatus(u, "deactivate")} disabled={!!regBusy[u.id]} className="btn-ghost text-xs" data-testid={`reg-deactivate-${u.id}`}>Desativar conta</button>
                      ) : (
                        <button onClick={() => setUserStatus(u, "reactivate")} disabled={!!regBusy[u.id]} className="btn-primary text-xs" data-testid={`reg-reactivate-${u.id}`}>Reativar conta</button>
                      )}
                      <label className="text-xs brand-muted flex items-center gap-2 ml-auto">
                        Tipo:
                        <select value={u.role === "b2b" ? "b2b" : "customer"} onChange={(e) => setUserRole(u, e.target.value)} className="input-line py-1 text-xs" data-testid={`reg-role-select-${u.id}`}>
                          <option value="customer">B2C / Particular</option>
                          <option value="b2b">B2B / Profissional</option>
                        </select>
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ============ EMAILS ============ */}
      {tab === "emails" && (
        <form onSubmit={saveEmailTpls} className="space-y-8 max-w-4xl" data-testid="tab-emails-content">
          <div>
            <p className="brand-muted text-sm">
              Personalize os emails automáticos enviados aos clientes. Use as variáveis <code className="text-xs">{"{{name}}"}</code>, <code className="text-xs">{"{{email}}"}</code>{" "}
              (no email de recusa também <code className="text-xs">{"{{reason}}"}</code>). Os emails são enviados via <strong>Resend</strong>.
            </p>
          </div>

          {[
            { key: "welcome", title: "Email de boas-vindas (após registo)", info: "Enviado quando o cliente submete o pedido de registo." },
            { key: "approved", title: "Email de aprovação", info: "Enviado quando o admin aprova o pedido de registo." },
            { key: "rejected", title: "Email de recusa", info: "Enviado quando o admin recusa o pedido. Suporta {{reason}}." },
            { key: "payment_confirmed", title: "Email de pagamento recebido", info: "Enviado uma única vez quando marca a encomenda como paga. Suporta {{order_number}}, {{total}}, {{items_html}}." },
          ].map((t) => (
            <section key={t.key} className="brand-surface p-6 border brand-border" data-testid={`email-tpl-${t.key}`}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-serif text-xl brand-text">{t.title}</h3>
                  <p className="text-xs brand-muted mt-1">{t.info}</p>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!emailTpls[t.key]?.enabled}
                    onChange={(e) => setEmailTpls((cur) => ({ ...cur, [t.key]: { ...cur[t.key], enabled: e.target.checked } }))}
                    data-testid={`email-tpl-${t.key}-enabled`}
                  />
                  Ativo
                </label>
              </div>
              <div className="space-y-3 mt-4">
                <div>
                  <label className="label-sm">Assunto</label>
                  <input
                    value={emailTpls[t.key]?.subject || ""}
                    onChange={(e) => setEmailTpls((cur) => ({ ...cur, [t.key]: { ...cur[t.key], subject: e.target.value } }))}
                    className="input-line mt-2"
                    data-testid={`email-tpl-${t.key}-subject`}
                  />
                </div>
                <div>
                  <label className="label-sm">Corpo do email (HTML)</label>
                  <textarea
                    value={emailTpls[t.key]?.body_html || ""}
                    onChange={(e) => setEmailTpls((cur) => ({ ...cur, [t.key]: { ...cur[t.key], body_html: e.target.value } }))}
                    className="input-line mt-2 min-h-[200px] font-mono text-xs"
                    data-testid={`email-tpl-${t.key}-body`}
                  />
                </div>
              </div>
            </section>
          ))}

          {tplsMsg && <p className="text-sm" style={{ color: tplsMsg.includes("sucesso") ? "#4A6B53" : "#9E4747" }} data-testid="email-tpls-msg">{tplsMsg}</p>}

          <div className="flex gap-3 sticky bottom-4 brand-surface p-4 border brand-border">
            <button type="submit" disabled={savingTpls} className="btn-primary" data-testid="btn-save-email-tpls">
              <Save size={16} /> {savingTpls ? "A guardar..." : "Guardar templates"}
            </button>
          </div>
        </form>
      )}

      {/* ============ BROADCAST ============ */}
      {tab === "broadcast" && (
        <form onSubmit={sendBroadcast} className="space-y-6 max-w-3xl" data-testid="tab-broadcast-content">
          <div>
            <h2 className="font-serif text-2xl brand-text mb-2">Enviar email a todos os clientes</h2>
            <p className="brand-muted text-sm">Envia um email a todos os clientes aprovados. Pode anexar ficheiros (PDF, imagens...) até 10 MB no total.</p>
          </div>

          <div>
            <label className="label-sm">Assunto</label>
            <input required value={broadcast.subject} onChange={(e) => setBroadcast({ ...broadcast, subject: e.target.value })} className="input-line mt-2" data-testid="broadcast-subject" />
          </div>

          <div>
            <label className="label-sm">Corpo do email (HTML permitido)</label>
            <textarea required value={broadcast.body_html} onChange={(e) => setBroadcast({ ...broadcast, body_html: e.target.value })} className="input-line mt-2 min-h-[200px]" placeholder="Escreva o email... pode usar {{name}} para personalizar." data-testid="broadcast-body" />
          </div>

          <div>
            <label className="label-sm">Anexos (opcional)</label>
            <div className="mt-2 space-y-2">
              {broadcast.attachments.map((a, i) => (
                <div key={i} className="brand-surface p-3 border brand-border flex items-center gap-3" data-testid={`broadcast-att-${i}`}>
                  <FileText size={16} className="brand-primary-text" />
                  <p className="flex-1 text-sm">{a.filename}</p>
                  <p className="text-xs brand-muted">{(a.size / 1024).toFixed(0)} KB</p>
                  <button type="button" onClick={() => removeAttachment(i)} className="text-xs text-red-700 underline">Remover</button>
                </div>
              ))}
              <label className="flex items-center justify-center gap-2 border-2 border-dashed brand-border rounded-sm p-4 cursor-pointer hover:brand-surface text-sm brand-muted" data-testid="broadcast-add-file">
                <Upload size={16} /> Adicionar ficheiro(s)
                <input type="file" multiple onChange={addBroadcastAttachment} className="hidden" />
              </label>
            </div>
          </div>

          {broadcastMsg && <p className="text-sm" style={{ color: broadcastMsg.includes("enviado") ? "#4A6B53" : "#9E4747" }} data-testid="broadcast-msg">{broadcastMsg}</p>}

          <button type="submit" disabled={broadcasting} className="btn-primary inline-flex items-center gap-2" data-testid="btn-send-broadcast">
            <Send size={14} /> {broadcasting ? "A enviar..." : "Enviar a todos os clientes"}
          </button>
        </form>
      )}

      {tab === "settings" && (
        <form onSubmit={saveSettings} className="space-y-10 max-w-4xl" data-testid="settings-form">
          {/* Informação da loja */}
          <section>
            <h2 className="font-serif text-2xl brand-text mb-2">Informação da loja</h2>
            <p className="text-sm brand-muted mb-6">Estes dados aparecem no rodapé e nas comunicações com o cliente.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label-sm">Nome da loja</label>
                <input required value={settings.store_name} onChange={(e) => setS("store_name", e.target.value)} className="input-line mt-2" data-testid="set-store-name" />
              </div>
              <div className="md:col-span-2">
                <label className="label-sm">Slogan / Tagline</label>
                <input value={settings.tagline} onChange={(e) => setS("tagline", e.target.value)} className="input-line mt-2" data-testid="set-tagline" />
              </div>
              <div>
                <label className="label-sm">Email de contacto</label>
                <input type="email" value={settings.email} onChange={(e) => setS("email", e.target.value)} className="input-line mt-2" data-testid="set-email" />
              </div>
              <div>
                <label className="label-sm">Telefone</label>
                <input value={settings.phone} onChange={(e) => setS("phone", e.target.value)} className="input-line mt-2" placeholder="+351 ..." data-testid="set-phone" />
              </div>
              <div className="md:col-span-2">
                <label className="label-sm">Morada</label>
                <input value={settings.address} onChange={(e) => setS("address", e.target.value)} className="input-line mt-2" data-testid="set-address" />
              </div>
              <div>
                <label className="label-sm">NIF</label>
                <input value={settings.nif} onChange={(e) => setS("nif", e.target.value)} className="input-line mt-2" data-testid="set-nif" />
              </div>
            </div>
          </section>

          {/* Dados bancários */}
          <section>
            <h2 className="font-serif text-2xl brand-text mb-2">Dados bancários</h2>
            <p className="text-sm brand-muted mb-6">Utilizados em transferências bancárias e referência interna. Não são partilhados publicamente.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-sm">Banco</label>
                <input value={settings.bank_name} onChange={(e) => setS("bank_name", e.target.value)} className="input-line mt-2" data-testid="set-bank-name" />
              </div>
              <div>
                <label className="label-sm">IBAN</label>
                <input value={settings.iban} onChange={(e) => setS("iban", e.target.value)} className="input-line mt-2 font-mono" placeholder="PT50 0000 0000 0000 0000 0000 0" data-testid="set-iban" />
              </div>
            </div>
          </section>

          {/* Redes sociais */}
          <section>
            <h2 className="font-serif text-2xl brand-text mb-2">Redes sociais</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-sm">Instagram</label>
                <input value={settings.instagram} onChange={(e) => setS("instagram", e.target.value)} className="input-line mt-2" placeholder="@auandco" data-testid="set-instagram" />
              </div>
              <div>
                <label className="label-sm">Facebook</label>
                <input value={settings.facebook} onChange={(e) => setS("facebook", e.target.value)} className="input-line mt-2" placeholder="auandco" data-testid="set-facebook" />
              </div>
            </div>
          </section>

          {/* Envios */}
          <section>
            <h2 className="font-serif text-2xl brand-text mb-2">Envios e devoluções</h2>
            <p className="text-sm brand-muted mb-6">Valores apresentados aos clientes no checkout e nas páginas de produto.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label-sm">Portes base (€)</label>
                <input type="number" step="0.01" value={settings.shipping_cost} onChange={(e) => setS("shipping_cost", e.target.value)} className="input-line mt-2" data-testid="set-shipping-cost" />
              </div>
              <div>
                <label className="label-sm">Envio grátis a partir de (€)</label>
                <input type="number" step="0.01" value={settings.free_shipping_threshold} onChange={(e) => setS("free_shipping_threshold", e.target.value)} className="input-line mt-2" data-testid="set-shipping-threshold" />
              </div>
              <div>
                <label className="label-sm">Dias para devolução</label>
                <input type="number" value={settings.return_policy_days} onChange={(e) => setS("return_policy_days", e.target.value)} className="input-line mt-2" data-testid="set-return-days" />
              </div>
            </div>
          </section>

          {settingsMsg && (
            <p className={`text-sm ${settingsMsg.includes("sucesso") ? "" : ""}`} style={{ color: settingsMsg.includes("sucesso") ? "#4A6B53" : "#9E4747" }} data-testid="settings-msg">
              {settingsMsg}
            </p>
          )}

          <div className="flex gap-3 sticky bottom-4 brand-surface p-4 border brand-border">
            <button type="submit" disabled={savingSettings} className="btn-primary" data-testid="btn-save-settings">
              <Save size={16} /> {savingSettings ? "A guardar..." : "Guardar definições"}
            </button>
          </div>
        </form>
      )}
    </main>
  );
}
